import React, { useState, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { 
  useListImages, 
  useUploadImages, 
  useGroupImages, 
  useUngroupImages,
  getListImagesQueryKey
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { ImageCard } from "@/components/ImageCard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { UploadCloud, Layers, Unlock, ImagePlus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export default function UploadPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [uploadProgress, setUploadProgress] = useState(0);

  // Queries & Mutations
  const { data: listData, isLoading } = useListImages();
  const images = useMemo(() => listData?.images || [], [listData]);

  const uploadMutation = useUploadImages({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListImagesQueryKey() });
        setUploadProgress(0);
        toast({ title: "Upload complete", description: "Images added successfully." });
      },
      onError: (err) => {
        setUploadProgress(0);
        toast({ title: "Upload failed", description: String(err), variant: "destructive" });
      }
    }
  });

  const groupMutation = useGroupImages({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListImagesQueryKey() });
        setSelectedIds(new Set());
        toast({ title: "Grouped successfully", description: "Images combined into a multi-page recipe." });
      }
    }
  });

  const ungroupMutation = useUngroupImages({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListImagesQueryKey() });
        setSelectedIds(new Set());
        toast({ title: "Ungrouped", description: "Images separated successfully." });
      }
    }
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    // Fake progress for UI
    setUploadProgress(10);
    const interval = setInterval(() => {
      setUploadProgress(p => Math.min(p + 10, 90));
    }, 200);

    const batchId = uuidv4();
    uploadMutation.mutate(
      { data: { images: acceptedFiles, batchId } },
      { onSettled: () => clearInterval(interval) }
    );
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif']
    },
    maxFiles: 50
  });

  const toggleSelection = (id: number, selected: boolean) => {
    const next = new Set(selectedIds);
    if (selected) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  };

  const handleGroup = () => {
    if (selectedIds.size < 2) return;
    groupMutation.mutate({ data: { imageIds: Array.from(selectedIds) } });
  };

  const handleUngroup = () => {
    if (selectedIds.size === 0) return;
    const firstSelectedId = Array.from(selectedIds)[0];
    const firstImg = images.find(i => i.id === firstSelectedId);
    const groupId = firstImg?.groupId;
    if (!groupId) return;
    ungroupMutation.mutate({ groupId });
  };

  const hasGroupedSelected = useMemo(() => {
    return Array.from(selectedIds).some(id => {
      const img = images.find(i => i.id === id);
      return img?.groupId !== null && img?.groupId !== undefined;
    });
  }, [selectedIds, images]);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header Section */}
        <div>
          <h2 className="text-3xl font-serif font-bold text-foreground">Digitize Recipes</h2>
          <p className="text-muted-foreground mt-2 text-lg">
            Upload handwritten or printed recipes. Select multiple images to group them into a single multi-page recipe.
          </p>
        </div>

        {/* Dropzone */}
        <Card className={cn(
          "border-2 border-dashed transition-all duration-300 ease-out bg-card/50",
          isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        )}>
          <div {...getRootProps()} className="p-12 text-center cursor-pointer flex flex-col items-center justify-center min-h-[200px]">
            <input {...getInputProps()} />
            <div className="bg-primary/10 p-4 rounded-full mb-4 text-primary">
              <UploadCloud className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-medium mb-2">
              {isDragActive ? "Drop recipes here..." : "Drag & drop recipe photos"}
            </h3>
            <p className="text-muted-foreground mb-4">
              Supports JPEG, PNG, WebP, HEIC (Max 50 files)
            </p>
            <Button variant="outline" className="pointer-events-none">
              Browse Files
            </Button>
          </div>
          {uploadMutation.isPending && (
            <div className="px-6 pb-6">
              <div className="flex justify-between text-sm mb-2 text-muted-foreground">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}
        </Card>

        {/* Action Bar */}
        <div className="flex items-center justify-between sticky top-[72px] z-40 bg-background/80 backdrop-blur-md py-4 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center gap-4">
            <h3 className="text-xl font-serif font-bold">
              Uploaded Recipes <span className="text-muted-foreground text-base font-sans font-normal ml-2">({images.length})</span>
            </h3>
            {selectedIds.size > 0 && (
              <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                {selectedIds.size} selected
              </span>
            )}
          </div>

          <AnimatePresence>
            {selectedIds.size > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center gap-2"
              >
                {selectedIds.size >= 2 && (
                  <Button onClick={handleGroup} disabled={groupMutation.isPending} className="gap-2">
                    <Layers className="w-4 h-4" />
                    Group as Multi-Page
                  </Button>
                )}
                {hasGroupedSelected && (
                  <Button onClick={handleUngroup} variant="secondary" disabled={ungroupMutation.isPending} className="gap-2">
                    <Unlock className="w-4 h-4" />
                    Ungroup
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setSelectedIds(new Set())}>
                  Cancel
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse bg-muted rounded-xl aspect-[3/4]" />
            ))}
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-24 px-4">
            <img 
              src={`${import.meta.env.BASE_URL}images/empty-binder.png`}
              alt="Empty recipe binder"
              className="w-48 h-auto mx-auto mb-6 opacity-80"
            />
            <h3 className="text-2xl font-serif text-foreground mb-2">No recipes yet</h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              Upload your first batch of handwritten or printed recipes to begin the digitization process.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 pb-20">
            {images.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                isSelected={selectedIds.has(image.id)}
                onSelect={toggleSelection}
                selectionMode={selectedIds.size > 0}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
